import React, { useEffect, useRef, useState } from "react";
import { Layout, Row, Col, Pagination } from "antd";
import { IPriceComparisonListData, IPriceComparisonListing } from "models/interfaces";
import PriceComparisonTable from "./PriceComparisonTable";
import { AppSetting } from "utils/appSettings";
import { ICustomError } from "models/interfaces/ICustomError";
import { ZonesService } from "services/SkyboxServices";
import { LoggerService } from "sharedServices";
import { isMobile } from "react-device-detect";
import { formatThousandSeparator } from "utils/commonFunction";
import AppliedFilters from "utils/commonFilter/AppliedFilters";

const PriceComparisonListings: React.FC<{
  searchQuery: any;
  queryParams: any;
  setQueryParams: Function;
  filtersAppliedWithValues: any[];
}> = ({ searchQuery, queryParams, setQueryParams, filtersAppliedWithValues }) => {
  const [showLoader, setShowLoader] = useState(true);
  const [dataFetchError, setDataFetchError] = useState<ICustomError | null>(
    null
  );
  const [priceComparisonData, setPriceComparisonData] =
    useState<IPriceComparisonListData | null>(null);
  // Guards against out-of-order responses: if the user changes a filter
  // before a prior fetch resolves, both requests are in flight, and whichever
  // happens to resolve LAST would otherwise overwrite the table with stale
  // data even though it isn't the most recent request. Only the response
  // matching the latest issued request is applied.
  const latestRequestId = useRef(0);

  const getPriceComparisonListings = async () => {
    const requestId = ++latestRequestId.current;
    try {
      setShowLoader(true);
      const response = await new ZonesService().getPriceComparisonListings({
        ...queryParams,
      });
      if (requestId !== latestRequestId.current) {
        return;
      }
      setPriceComparisonData(response.data.data);
      setShowLoader(false);
    } catch (error: any) {
      if (requestId !== latestRequestId.current) {
        return;
      }
      setShowLoader(false);
      await new LoggerService().log({
        payload: error,
        function_name: "getPriceComparisonListings",
      });
      setDataFetchError({
        ...error,
        refreshAction: getPriceComparisonListings,
      });
    }
  };

  useEffect(() => {
    // Guard on a field that's only populated once FilterDrawer's own default
    // date ranges have loaded and auto-applied (same pattern Zone Listings'
    // AllZoneListingsComponent uses: `if (queryParams.event_date_from)`).
    // queryParams is truthy from the very first render (seeded with bare
    // defaultQueryParams), so a plain truthiness check fires an extra,
    // premature fetch with no date filters, which then gets immediately
    // superseded once the real defaults apply -- wasteful and exactly the
    // double-call this guard avoids.
    queryParams.event_date_from && getPriceComparisonListings();
  }, [queryParams]);

  useEffect(() => {
    if (searchQuery !== null) {
      setQueryParams({
        ...queryParams,
        page_index: AppSetting.DEFAULT_PAGE_INDEX,
        search_text: searchQuery,
      });
    }
  }, [searchQuery]);

  const getFormattedTotalCount = (count: number) => {
    const formattedCount = formatThousandSeparator(count);
    return count === 1
      ? `${formattedCount} Record Found`
      : `${formattedCount} Records Found`;
  };

  const handlePageChange = (pageIndex: number) => {
    setQueryParams({
      ...queryParams,
      page_index: pageIndex,
    });
  };

  const handleRowUpdated = (
    purchaseLineId: number,
    changes: Partial<IPriceComparisonListing>
  ) => {
    setPriceComparisonData((prev) => {
      if (!prev || !prev.data) return prev;
      return {
        ...prev,
        data: prev.data.map((listing) =>
          listing.purchase_line_id === purchaseLineId
            ? { ...listing, ...changes }
            : listing
        ),
      };
    });
  };

  return (
    <React.Fragment>
      <Layout className="mainInnerLayout pb-0 reduceTopSpace">
        <div className="innerHeader">
          <Row className="flx justifyContentBetween alignItemsCenter">
            <Col xl={12} xxl={16}>
              <AppliedFilters
                appliedFilters={filtersAppliedWithValues ?? []}
                resetUrl={null}
              />
            </Col>
            <Col xs={24} md={24} xl={12} xxl={8}>
              <ul className="innerHeaderRight">
                {!showLoader &&
                priceComparisonData &&
                priceComparisonData?.count ? (
                  <React.Fragment>
                    <li>
                      <h4>
                        {getFormattedTotalCount(priceComparisonData?.count)}
                      </h4>
                    </li>
                    <li>
                      <Pagination
                        className="pagination"
                        simple={isMobile}
                        size={isMobile ? "small" : undefined}
                        hideOnSinglePage={true}
                        defaultCurrent={1}
                        current={queryParams?.page_index}
                        total={priceComparisonData?.count}
                        pageSize={AppSetting.PAGE_SIZE}
                        onChange={handlePageChange}
                        showSizeChanger={false}
                      />
                    </li>
                  </React.Fragment>
                ) : (
                  <div style={{ height: 21 }}></div>
                )}
              </ul>
            </Col>
          </Row>
        </div>
        <PriceComparisonTable
          showLoader={showLoader}
          dataFetchError={dataFetchError}
          listings={priceComparisonData?.data ?? null}
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          onRowUpdated={handleRowUpdated}
        />
      </Layout>
    </React.Fragment>
  );
};

export default PriceComparisonListings;
